import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useEmployees } from '@/hooks/useEmployees'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import { Button } from '@/components/ui/button'
import { maskCpf } from '@/components/form/masks'
import { formatBRL } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { EMPLOYEE_ROLE_LABELS, type Employee, type EmployeeRole } from '@/types/employee'
import type { Column } from '@/components/ui/data-table'

const selectClass = cn(
  'flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

type ActiveFilter = 'all' | 'active' | 'inactive'

const FILTER_LABELS: Record<ActiveFilter, string> = {
  all: 'Todos',
  active: 'Ativos',
  inactive: 'Desligados',
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR')
}

const COLUMNS: Column<Employee>[] = [
  { key: 'name', header: 'Nome' },
  { key: 'cpf', header: 'CPF', render: (row) => maskCpf(row.cpf) },
  {
    key: 'role',
    header: 'Cargo',
    render: (row) => EMPLOYEE_ROLE_LABELS[row.role],
  },
  {
    key: 'baseSalary',
    header: 'Salário base',
    className: 'hidden lg:table-cell',
    render: (row) => <span className="tabular-nums">{formatBRL(row.baseSalary)}</span>,
  },
  {
    key: 'hireDate',
    header: 'Admissão',
    className: 'hidden lg:table-cell',
    render: (row) => formatDate(row.hireDate),
  },
  {
    key: 'active',
    header: 'Status',
    render: (row) => <StatusBadge active={row.active} />,
  },
]

export function EmployeeListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<EmployeeRole | ''>('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [page, setPage] = useState(1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useEmployees({
    search,
    active: activeParam,
    ...(roleFilter && { role: roleFilter }),
    page,
  })

  function handleSearch(v: string) {
    setSearch(v)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Funcionários</h1>
          <p className="text-sm text-muted-foreground">
            Equipe da empresa (escritório, oficina, administrativo)
          </p>
        </div>
        <Button onClick={() => navigate('/employees/new')}>
          <Plus size={16} />
          Novo funcionário
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por nome ou CPF…"
          className="flex-1"
        />
        <select
          className={selectClass}
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value as EmployeeRole | '')
            setPage(1)
          }}
        >
          <option value="">Todos os cargos</option>
          {(Object.keys(EMPLOYEE_ROLE_LABELS) as EmployeeRole[]).map((r) => (
            <option key={r} value={r}>
              {EMPLOYEE_ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setActiveFilter(f)
                setPage(1)
              }}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/employees/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhum funcionário cadastrado"
        emptyDescription="Comece cadastrando o primeiro funcionário."
        emptyAction={
          <Button onClick={() => navigate('/employees/new')}>
            <Plus size={16} />
            Novo funcionário
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
